import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { t } from "ttag";
import { Spinner, Typography } from "@coolremote/shared";
import { useStoreActions } from "../../../models/RootStore";
import DeviceConfiguration from "./DeviceConfiguration";
import DeviceConfigSideBar from "./DeviceConfigSideBar";
import styles from "./DeviceConfigurationPage.module.css";

const DeviceConfigurationPage: React.FC = () => {
  const { deviceId } = useParams<{ deviceId: string }>();
  const location = useLocation();
  const scrollToField = new URLSearchParams(location.search).get("scrollTo") ?? undefined;
  const getDeviceTree = useStoreActions((actions) => actions.getDeviceTree);
  const getDeviceDetails = useStoreActions((actions) => actions.getDeviceDetails);
  const getSiteMgmtDataBySiteId = useStoreActions((actions) => actions.sites.getSiteMgmtDataBySiteId);
  const controlDevice = useStoreActions((actions) => actions.devices.controlDevice);
  const [device, setDevice] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [promptColor, setPromptColor] = useState("#f05347");

  const fetchDevice = async () => {
    setIsLoading(true);
    try {
      const deviceTree = await getDeviceTree({ id: deviceId });
      const siteMgmtData = await getSiteMgmtDataBySiteId(deviceTree.site);
      const deviceWithPermissions = siteMgmtData?.devices?.find((d: any) => d.id === deviceId);
      setDevice(deviceWithPermissions || deviceTree);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDevice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const fetchDeviceDetails = useCallback(() => getDeviceDetails({ id: deviceId }), [deviceId, getDeviceDetails]);

  const runTerminalCommand = (cmd: any, print: any) => {
    setPromptColor("black");
    const command = cmd.slice(0).join(" ");
    controlDevice({ deviceId, command })
      .then((res: any) => {
        print(res);
        setPromptColor("#f05347");
      })
      .catch((err: any) => {
        print(typeof err === "string" ? err : JSON.stringify(err));
        setPromptColor("#f05347");
      });
  };

  const terminalConfig = {
    promptColor,
    style: {
      maxHeight: "100%",
      fontSize: "1.125em",
      width: "100%"
    },
    commands: {
      help: runTerminalCommand
    },
    commandPassThrough: runTerminalCommand
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={styles.centeredFull}>
          <Spinner size={32} />
        </div>
      );
    }

    if (!device) {
      return (
        <div className={styles.centeredFull}>
          <Typography variant="h6">{t`Device not found`}</Typography>
        </div>
      );
    }

    if (!device.permissions?.canReadDeviceConfig) {
      return (
        <div className={styles.centeredFull}>
          <Typography variant="h6">{t`You don't have permission to view this device's configuration`}</Typography>
        </div>
      );
    }

    return (
      <DeviceConfiguration
        deviceId={deviceId}
        isConnected={device.isConnected}
        terminalConfig={terminalConfig}
        canUpdateDeviceConfig={!!device.permissions?.canUpdateDeviceConfig}
        onRetryConnection={fetchDevice}
        scrollToField={scrollToField}
      />
    );
  };

  return (
    <div className={styles.pageContainer}>
      <DeviceConfigSideBar getDeviceDetails={fetchDeviceDetails} />
      <div className={styles.configContainer}>
        {isLoading || !device || !device.permissions?.canReadDeviceConfig ? (
          <div className={styles.centeredFull}>
            {renderContent()}
          </div>
        ) : (
          renderContent()
        )}
      </div>
    </div>
  );
};

export default DeviceConfigurationPage;
